import { useState, useCallback, useContext, useEffect } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useAutocompleteSuggestions } from "../hooks/useAutocompleteSuggestions";

import Button from "../components/Button";
import { ReactComponent as IconArrowRightCircle } from "../img/icons/arrow-right-circle.svg";

import SimulationContext from "../contexts/Simulation";

const PlaceAutocomplete = ({ onPlaceSelect, initAddr }) => {
  const places = useMapsLibrary("places");
  const [inputValue, setInputValue] = useState(initAddr ?? '');
  const [placeSelected, setPlaceSelected] = useState(false);

  const {suggestions, resetSession} = useAutocompleteSuggestions(
    inputValue, {
      language: 'fr',
      region: 'FR',
      includedRegionCodes: ['FR']
    }
  );
  const [simParams, setSimParams] = useContext(SimulationContext);

  const handleInput = useCallback((event) => {
    setPlaceSelected(false);
    setInputValue(event.target.value);
  }, []);

  const handleSuggestionClick = useCallback(
    async (suggestion) => {
      if (!places) return;
      if (!suggestion.placePrediction) return;

      const place = suggestion.placePrediction.toPlace();

      await place.fetchFields({
        fields: [
          'viewport',
          'location',
          'addressComponents',
          'formattedAddress'
        ]
      });

      setPlaceSelected(true);
      setInputValue(place.formattedAddress);

      // calling fetchFields invalidates the session-token, so we now have to call
      // resetSession() so a new one gets created for further search
      resetSession();

      onPlaceSelect(place);
      setSimParams({ ...simParams, location: {
        formatted: place.formattedAddress,
        components: place.addressComponents,
        location: place.location
      }});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [places, onPlaceSelect]
  );

  useEffect(() => {
    if (initAddr && (suggestions.length === 1))
      handleSuggestionClick(suggestions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions]);

  return (
    <>
      <input 
        type="text" 
        id="searchinput" 
        value={inputValue}
        onInput={event => handleInput(event)}
        placeholder="Saisissez une addresse"
      />
      {((suggestions.length > 0 && !initAddr) || (initAddr && suggestions.length > 1)) && !placeSelected && (
        <ul className="places-list">
          {suggestions.map((suggestion, index) => {
            return (
              <li
                key={index}
                className="places-list-item"
                onClick={() => handleSuggestionClick(suggestion)}>
                {suggestion.placePrediction?.text.text}
              </li>
            );
          })}
        </ul>
      )}
      <Button 
        IconAfter={IconArrowRightCircle} 
        variant={placeSelected ? '' : ' outlined thin light'} 
        link={placeSelected ? '/simulateur/localisation' : null}
      >Mon devis</Button>
    </>
  )
}

const SearchInput = ({ children, onPlaceSelect, initAddr }) => {
  return (
    <PlaceAutocomplete onPlaceSelect={onPlaceSelect} initAddr={initAddr} />
  )
}

export default SearchInput;